/**
 * Slightly customized stab-searchbox. Adds additional UI events to certain
 * internal dom elements to smoothe the experience on mobile devices.
 */

import SearchBox from '@homeaway/stab-searchbox';
import multivtest from '@homeaway/stab-multivtest-utils';
import $ from 'jquery';
import Backbone from 'backbone';
import throttle from 'lodash.throttle';
import Handlebars from 'handlebars';
import getProctorMatrix from './proctor';
import stabSearchboxMessage from '../helpers/stabSearchboxMessage';
import 'bootstrap-button';
import getSearchBoxOptions from './getSearchBoxOptions';
import moment from 'moment';

Handlebars.registerHelper('stabSearchboxMessage', stabSearchboxMessage);
window.$ = $;

/**
 * Listens to a 'modalOpen' event from the stab-searchbox model
 * Toggles class on an exterior contaienr so we can style the
 * form suitably.
 *
 * @param  {jQuery selection}   $target    external container on which to toggle class
 * @param  {string}             className  class name to toggle on $target
 */
function initBodyClassListener($target, className) {
  let lastVal;

  className = className || 'search-modal-open';

  function handler(model) {
    let isOpen = !!model.get('modalOpen');
    if (isOpen !== lastVal) {
      $target.toggleClass(className, isOpen);
      lastVal = isOpen;
    }
  }

  this.model.on('change', handler);
}

/**
 * Listens for the 'beforeSubmit' event and toggles the search button loader.
 */
function initFormSubmitLoadingButton(state) {
  const view = this.view;
  const gs = view.guestSelector;

  view.elements.$searchSubmit = view.$(view.selectors.searchSubmit);
  view.elements.$searchSubmit.attr('data-loading-animation', 'true');

  view.on('beforeSearch', () => {
    view.elements.$searchSubmit.button('loading');
  });
  if (state.system.isMobile) {
    const onDoneClick = gs.view.onDoneClick;
    const elements = [{
      element: view.elements.$keywords,
      test: () => !view.elements.$keywords.val()
    }, {
      element: view.elements.$startCalendar,
      test: () => !view.model.getArrival()
    }, {
      element: view.elements.$endCalendar,
      test: () => !view.model.getDeparture()
    }];

    Object.assign({}, gs.view.prototype, Backbone.Events);
    gs.view.onDoneClick = () => gs.view.trigger('doneClick');

    if (typeof onDoneClick === 'function') {
      gs.view.on('doneClick', onDoneClick);
    }

    gs.view.on('doneClick', () => {
      for (let i = 0; i < elements.length; i++) {
        const el = elements[i];
        if (el.test()) {
          setTimeout(() => el.element.focus(), 1);
          return;
        }
      }

      view.search();
    });
  }
}


/**
 * Apply a blur event to typeahead text input. On a mobile device
 * this will cause the keyboard to dissappear
 *
 * @param  {jQuery selection}  $typeaheadInput  selector identifying the text input
 */
function initTypeaheadBlur(searchBoxOptions, $typeaheadInput) {
  function handler() {
    if (searchBoxOptions.enableFocusFix === true) {
      $typeaheadInput.blur();
    }
  }
  $typeaheadInput.typeahead('close'); // fix for hiding typeahead suggestion list on first page load
  $typeaheadInput.bind('typeahead:selected', throttle(handler, 100));
}

/**
 * Monitors changes to the search parameters. Applies a valid class
 * to the UI which will let us style and manipulate the search form
 *
 * @param  {jQuery selection}   $target      target selection to toggle class
 * @param  {string}             className    class to toggle on/of
 */
function initFormValidityListener($target, className) {
  let lastVal;
  className = className || 'valid';

  function handler(isValid) {
    if (isValid !== lastVal) {
      $target.toggleClass(className, isValid);
      lastVal = isValid;
    }
  }

  this.model.on('paramsValid', handler);
}

/**
 * factory function generates and configures a stab-searchbox
 * @param  {object}  options  custom options
 * @return {object}           stab-searchbox instance
 */
function customSearch(state) {
  const { system: { isMobile }} = state;
  const testMatrix = getProctorMatrix(isMobile);

  moment.locale(state.intl.hrefLang);
  multivtest.initialize(testMatrix);

  const searchBoxOptions = getSearchBoxOptions(state);
  const searchbox = new SearchBox(searchBoxOptions);
  const $keywords = searchbox.view.elements.$keywords;

  if (isMobile) {
    // On keyword focus, move document position up to expose search button
    $keywords.on('focus', () => {
      try {
        const stabSearchBoxTop = document.getElementById('stab-searchbox').getBoundingClientRect().top;
        window.scrollTo(0, stabSearchBoxTop + window.scrollY);
      } catch (e) {
        console.error(e); //eslint-disable-line
      }
    });
  }

  try {
    initBodyClassListener.call(searchbox, $(searchBoxOptions.externalContainer));
    initTypeaheadBlur.call(searchbox, searchBoxOptions, searchbox.view.elements.$keywords);
    initFormValidityListener.call(searchbox, searchbox.view.$el);
    initFormSubmitLoadingButton.call(searchbox, state);
    window.ha.userTiming.mark('primary.action.rendered');
  } catch (err) {
    console.warn("Unable to customize search interface."); // eslint-disable-line
  }

  return searchbox;
}

export default customSearch;



// WEBPACK FOOTER //
// ./src/universal/utils/initSearchbox.js